import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import { collection, query, orderBy, limit, onSnapshot, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { getMintEngineUrl } from '../../config';

const MintProgress = ({ mintData }) => {
  const [currentStatus, setCurrentStatus] = useState("Initialisation...");
  const [logs, setLogs] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isMinting, setIsMinting] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [mintResult, setMintResult] = useState(null);

  useEffect(() => {
    if (!mintData) {
      setCurrentStatus("Erreur: Données manquantes");
      return;
    }

    // Lancer le processus d'upload et mint
    startMintProcess();
  }, [mintData]);

  // Écouter les logs depuis Firebase - démarrer dès le montage du composant
  useEffect(() => {
    // Calculer le début et la fin de la journée (aujourd'hui)
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    const startOfDayTimestamp = Timestamp.fromDate(startOfDay);
    const endOfDayTimestamp = Timestamp.fromDate(endOfDay);

    const logsRef = collection(db, 'mint-logs');
    // Filtrer les logs de la journée et les trier par date décroissante
    const q = query(
      logsRef,
      where('date', '>=', startOfDayTimestamp),
      where('date', '<=', endOfDayTimestamp),
      orderBy('date', 'desc'),
      limit(100)
    );

    console.log('Écoute des logs démarrée pour la journée:', startOfDay.toLocaleString('fr-FR'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const newLogs = [];
        snapshot.forEach((doc) => {
          newLogs.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        console.log('Logs reçus:', newLogs.length);
        setLogs(newLogs);

        // Mettre à jour le statut avec le dernier log
        if (newLogs.length > 0) {
          const latestLog = newLogs[0];
          if (latestLog.content) {
            setCurrentStatus(latestLog.content);
          }
        }
      },
      (error) => {
        console.error('Erreur lors de l\'écoute des logs:', error);
      }
    );

    return () => unsubscribe();
  }, []); // Démarrer dès le montage, pas besoin d'attendre mintResult

  // Fonction pour formater le contenu des logs
  const formatLogContent = (content) => {
    if (!content) return '';

    // Si c'est une string, essayer de la parser comme JSON
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        // Si ce n'est pas du JSON valide, retourner tel quel
        return content;
      }
    }

    // Si c'est déjà un objet, le formater en JSON indenté
    if (typeof content === 'object') {
      return JSON.stringify(content, null, 2);
    }

    return String(content);
  };

  // Fonction pour vérifier si le contenu est du JSON
  const isJSON = (content) => {
    if (!content) return false;
    if (typeof content === 'object') return true;
    if (typeof content === 'string') {
      try {
        JSON.parse(content);
        return true;
      } catch (e) {
        return false;
      }
    }
    return false;
  };

  const startMintProcess = async () => {
    try {
      // Étape 1: Upload des fichiers vers S3
      setCurrentStatus("Upload des images et métadonnées en cours sur S3...");
      setIsUploading(true);

      const apiUrl = getMintEngineUrl();
      const uploadResponse = await fetch(`${apiUrl}/upload-files`, {
        method: "POST",
        body: mintData.formDataToSend,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Erreur HTTP lors de l'upload: ${uploadResponse.status}`);
      }

      const uploadResultData = await uploadResponse.json();
      console.log("Résultat de l'upload:", uploadResultData);
      setUploadResult(uploadResultData);
      setIsUploading(false);

      // Étape 2: Appel à l'API /mint
      setCurrentStatus("Lancement du mint...");
      setIsMinting(true);

      // Utiliser la même URL que pour l'upload
      const mintApiUrl = getMintEngineUrl();
      const mintResponse = await fetch(`${mintApiUrl}/mint`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(mintData.mintRequestData),
      });

      if (!mintResponse.ok) {
        throw new Error(`Erreur HTTP lors du mint: ${mintResponse.status}`);
      }

      const mintResultData = await mintResponse.json();
      console.log("Résultat du mint:", mintResultData);
      setMintResult({ success: true, data: mintResultData });
      setIsMinting(false);
      setCurrentStatus("Mint lancé avec succès. Attente des logs...");

    } catch (error) {
      console.error("Erreur lors du processus de mint:", error);
      setCurrentStatus(`Erreur: ${error.message}`);
      setIsUploading(false);
      setIsMinting(false);
      setMintResult({ success: false, error: error.message });
    }
  };

  return (
    <div className="min-h-screen bg-[#00263a]">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold text-white mb-6">Mint en cours</h1>

        {/* Section Informations */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Informations</h2>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isUploading ? 'bg-yellow-500 animate-pulse' :
                uploadResult ? 'bg-green-500' : 'bg-gray-500'
                }`}></div>
              <p className="text-white">
                {isUploading
                  ? "Upload des images et métadonnées en cours sur S3..."
                  : uploadResult
                    ? `Upload terminé: ${uploadResult.total} fichier(s) uploadé(s)`
                    : "En attente de l'upload..."}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isMinting ? 'bg-yellow-500 animate-pulse' :
                mintResult?.success ? 'bg-green-500' :
                  mintResult?.success === false ? 'bg-red-500' : 'bg-gray-500'
                }`}></div>
              <p className="text-white">
                {isMinting
                  ? "Lancement du mint..."
                  : mintResult?.success
                    ? "Mint lancé avec succès"
                    : mintResult?.success === false
                      ? `Erreur: ${mintResult.error}`
                      : "En attente du mint..."}
              </p>
            </div>
            <div className="mt-4 p-3 bg-gray-700 rounded-md">
              <p className="text-sm text-gray-300 mb-2">Statut actuel:</p>
              <div className="bg-gray-800 rounded p-3 max-h-48 overflow-y-auto">
                <p className="text-white font-semibold whitespace-pre-wrap break-words">
                  {isJSON(currentStatus) ? formatLogContent(currentStatus) : currentStatus}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section Logs - Toujours afficher les logs de la journée */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-white mb-4">Logs du mint (journée en cours)</h2>
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-gray-400 italic">Aucun log disponible pour le moment...</p>
            ) : (
              logs.map((log) => {
                const logContent = log.content || log.message || JSON.stringify(log);
                const formattedContent = isJSON(logContent) ? formatLogContent(logContent) : logContent;

                return (
                  <div
                    key={log.id}
                    className="bg-gray-700 rounded-md p-4 text-sm"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-gray-300 text-xs">
                        {log.date?.toDate ?
                          log.date.toDate().toLocaleString('fr-FR') :
                          log.date ? new Date(log.date.seconds * 1000).toLocaleString('fr-FR') :
                            'Date inconnue'}
                      </span>
                      {log.level && (
                        <span className={`px-2 py-1 rounded text-xs ${log.level === 'error' ? 'bg-red-500' :
                          log.level === 'warning' ? 'bg-yellow-500' :
                            log.level === 'success' ? 'bg-green-500' :
                              'bg-blue-500'
                          }`}>
                          {log.level}
                        </span>
                      )}
                    </div>
                    <div className="bg-gray-800 rounded p-3 overflow-x-auto">
                      <pre className="text-white text-xs whitespace-pre-wrap break-words font-mono">
                        {formattedContent}
                      </pre>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MintProgress;
